// Leaf node: a single SamplePoint. Small, colored by status, with an
// overlayed "M" badge when source === 'manual'.

import { Handle, Position } from '@xyflow/react';
import type { SamplePoint } from '../types';
import { fallbackTheme } from './theme';
import { NODE_SIZES } from './provenanceLayout';

export interface SamplePointNodeData {
  point: SamplePoint;
}

export default function SamplePointNode({ data }: { data: SamplePointNodeData }) {
  const t = fallbackTheme;
  const { width, height } = NODE_SIZES.point;
  const p = data.point;
  const color = t.status[p.status];
  const isManual = p.source === 'manual';

  const inputsText = formatInputs(p.inputs);

  return (
    <div
      title={`${p.id}\nstatus: ${p.status}\nsource: ${p.source}\n${inputsText}`}
      style={{
        width,
        height,
        position: 'relative',
        background: t.surface,
        border: `2px solid ${color}`,
        borderRadius: 8,
        color: t.text,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        lineHeight: 1.1,
        padding: 4,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: color,
          marginBottom: 4,
        }}
      />
      <div style={{ fontSize: 10, color: t.textMuted, textAlign: 'center' }}>
        {shortId(p.id)}
      </div>
      {isManual && (
        <div
          aria-label="manual sample"
          style={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: t.manualBadge,
            color: '#0d1117',
            fontSize: 10,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${t.surface}`,
          }}
        >
          M
        </div>
      )}
      <Handle type="target" position={Position.Top} style={{ background: t.border }} />
    </div>
  );
}

function shortId(id: string): string {
  if (id.length <= 6) return id;
  const parts = id.split('_');
  const last = parts[parts.length - 1];
  return parts.length > 1 && last ? last.slice(0, 6) : id.slice(-6);
}

function formatInputs(inputs: Record<string, number>): string {
  return Object.entries(inputs)
    .slice(0, 4)
    .map(([k, v]) => `${k}=${typeof v === 'number' ? v.toFixed(2) : v}`)
    .join(', ');
}
